/**
 * Shared formatting utilities.
 * Import from here — do NOT duplicate in individual components.
 */

/**
 * Format an entry fee from cents to a display string with currency symbol.
 * ZAR → "R150", USD → "$150", others → "$X" (dollar sign fallback).
 */
export function formatEntryFee(cents: number, currencyCode: string): string {
  const symbol = currencyCode === 'ZAR' ? 'R' : '$';
  return `${symbol}${(cents / 100).toLocaleString('en-ZA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * hotfix-1-171: prize_description is admin-authored markdown. Full detail pages
 * render it as markdown, but compact card snippets (e.g. the winners list, the
 * partner QR caption) show plain text — strip the markdown syntax so customers
 * don't see stray `**`, `#`, or `-` markers in a clamped one-liner.
 *
 * This is a lightweight, presentation-only strip (not a full parser): removes
 * bold/italic/code markers, heading hashes, list bullets, links (keeps the text)
 * and collapses whitespace. Safe for short previews.
 */
export function stripMarkdown(input: string): string {
  if (!input) return '';
  return input
    // links/images: [text](url) → text  (drop leading ! for images)
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    // bold/italic/strikethrough/inline-code markers
    .replace(/(\*\*|__|\*|_|~~|`)/g, '')
    // leading heading hashes, blockquotes, and list markers per line
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
    .replace(/^[ \t]*>[ \t]?/gm, '')
    .replace(/^[ \t]*[-*+][ \t]+/gm, '')
    .replace(/^[ \t]*\d+\.[ \t]+/gm, '')
    // collapse remaining whitespace/newlines to single spaces
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Compute days/hours remaining until a draw date.
 * @param drawDate ISO date string
 * @param nowMs   Override for current time (injectable for testing). Defaults to Date.now().
 * @returns { days, hours } or null if date is invalid or already past.
 */
export function getCountdown(
  drawDate: string,
  nowMs: number = Date.now(),
): { days: number; hours: number } | null {
  const d = new Date(drawDate);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = d.getTime() - nowMs;
  if (diffMs <= 0) return null;
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  return { days: Math.floor(totalHours / 24), hours: totalHours % 24 };
}
